import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ChevronLeft, ChevronRight, Copy, Eye, Key, Pencil, Plug,
  Plus, RefreshCw, Search, Terminal, Trash2, Unplug, X,
} from 'lucide-react';
import { adminApi } from '../services/api';
import { PackageManager } from '../components/admin/PackageManager';
import { CommandTemplatesManager } from '../components/admin/CommandTemplatesManager';
import { AddHostAgentModal } from '../components/admin/AddHostAgentModal';
import { HostAgentDetailsModal } from '../components/admin/HostAgentDetailsModal';
import { OperationDiagnostics } from '../components/admin/OperationDiagnostics';
import { SSHModal } from '../components/admin/SSHModal';
import { CustomProductsManager } from '../components/admin/custom/CustomProductsManager';
import type { AdminHostAgent } from '../types/admin';
import { AdminHeader } from '../components/admin/AdminHeader';
import { HostLoadDisplay } from '../components/admin/HostLoadDisplay';
import { formatHostLoadBytes } from '../utils/hostLoad';

export function AdminPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('host-agents');
  const [showAgentForm, setShowAgentForm] = useState(false);
  const [editHostAgent, setEditHostAgent] = useState<AdminHostAgent | null>(null);
  const [detailAgentId, setDetailAgentId] = useState<string | null>(null);
  const [sshAgent, setSshAgent] = useState<AdminHostAgent | null>(null);
  const [oneTimeSecret, setOneTimeSecret] = useState<{ title: string; value: string } | null>(null);
  const [message, setMessage] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [filterName, setFilterName] = useState('');
  const [filterProvider, setFilterProvider] = useState('');
  const [filterIp, setFilterIp] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const queryClient = useQueryClient();

  const hostAgentsQuery = useQuery({
    queryKey: ['host-agents', { page, pageSize, filterActive, filterName, filterProvider, filterIp }],
    queryFn: async () => {
      const params: Record<string, unknown> = { page, pageSize };
      if (filterActive !== 'all') params.active = filterActive === 'active';
      if (filterName) params.name = filterName;
      if (filterProvider) params.provider = filterProvider;
      if (filterIp) params.ip = filterIp;
      return adminApi.getHostAgents(params) as Promise<{
        hostAgents: AdminHostAgent[];
        pagination: { page: number; pageSize: number; total: number; totalPages: number };
      }>;
    },
    refetchInterval: 5_000,
  });

  const actionMutation = useMutation({
    mutationFn: async ({ agent, action }: { agent: AdminHostAgent; action: string }) => {
      switch (action) {
        case 'check-health': return adminApi.checkHostAgentHealth(agent._id);
        case 'connect': return adminApi.initiateHostAgentConnection(agent._id);
        case 'disconnect': return adminApi.disconnectHostAgent(agent._id);
        case 'delete': return adminApi.deleteHostAgent(agent._id);
        default: throw new Error(`Unknown Agent action ${action}`);
      }
    },
    onSuccess: async (_, variables) => {
      setMessage({ kind: 'success', text: variables.action === 'check-health' ? 'Authenticated health check passed.' : `Agent ${variables.action} completed.` });
      await queryClient.invalidateQueries({ queryKey: ['host-agents'] });
    },
    onError: (error: any) => setMessage({
      kind: 'error',
      text: error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Agent action failed',
    }),
  });

  const hostAgents = hostAgentsQuery.data?.hostAgents || [];
  const pagination = hostAgentsQuery.data?.pagination;
  useEffect(() => {
    const requested = searchParams.get('tab');
    const nextTab = requested && ['host-agents', 'packages', 'custom-products', 'diagnostics', 'commands'].includes(requested)
      ? requested
      : 'host-agents';
    if (nextTab !== activeTab) setActiveTab(nextTab);
  }, [activeTab, searchParams]);

  const selectTab = (tabId: string) => {
    setActiveTab(tabId);
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      if (tabId === 'host-agents') next.delete('tab');
      else next.set('tab', tabId);
      if (tabId !== 'custom-products') {
        next.delete('productId');
        next.delete('section');
      }
      return next;
    }, { replace: true });
  };

  const setCustomProductNavigation = useCallback((productId: string | null, section: string) => {
    setSearchParams(current => {
      const next = new URLSearchParams(current);
      next.set('tab', 'custom-products');
      if (productId) next.set('productId', productId);
      else next.delete('productId');
      next.set('section', section);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const rotateToken = async (agent: AdminHostAgent) => {
    const supplied = prompt('Enter an existing 32+ character token, or leave blank to generate a new token. Cancel keeps the current token.');
    if (supplied === null) return;
    try {
      const result = await adminApi.rotateHostAgentToken(agent._id, supplied.trim() || undefined);
      setOneTimeSecret({ title: `Token for ${agent.name}`, value: result.agentToken });
      setMessage({ kind: 'success', text: 'Token rotated. Update HOST_AGENT_TOKEN before expecting reconnect.' });
      await queryClient.invalidateQueries({ queryKey: ['host-agents'] });
    } catch (error: any) {
      setMessage({ kind: 'error', text: error?.response?.data?.error || error?.message || 'Token rotation failed' });
    }
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <AdminHeader activeTab={activeTab} onSelectTab={selectTab} />

        {message && <div className={`mb-5 p-3 rounded border text-sm flex justify-between gap-3 ${message.kind === 'error' ? 'border-red-500/30 bg-red-500/10 text-red-300' : 'border-neon-500/20 bg-neon-500/10 text-neon-300'}`}><span>{message.text}</span><button onClick={() => setMessage(null)}><X className="w-4 h-4" /></button></div>}

        {activeTab === 'host-agents' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div><h2 className="section-title">Host Agent management</h2><p className="text-xs text-gray-500 mt-1">HTTP health and persistent Socket.IO state are reported separately.</p></div>
              <button onClick={() => { setEditHostAgent(null); setShowAgentForm(true); }} className="btn-primary"><Plus className="w-4 h-4" /> Add Agent</button>
            </div>

            <div className="card p-4">
              <div className="flex flex-wrap gap-3 items-end">
                <label className="w-32 text-xs text-gray-500">Status
                  <select className="input mt-1" value={filterActive} onChange={event => { setFilterActive(event.target.value as any); setPage(1); }}><option value="all">All</option><option value="active">Enabled</option><option value="inactive">Disabled</option></select>
                </label>
                <Filter label="Name" value={filterName} onChange={setFilterName} />
                <Filter label="Provider" value={filterProvider} onChange={setFilterProvider} />
                <Filter label="IP / hostname" value={filterIp} onChange={setFilterIp} />
                <button className="btn-secondary" onClick={() => { setPage(1); hostAgentsQuery.refetch(); }}><Search className="w-4 h-4" /> Search</button>
              </div>
            </div>

            {hostAgentsQuery.isError && <div className="p-4 border border-red-500/20 bg-red-500/10 rounded-lg text-red-300 text-sm">{(hostAgentsQuery.error as any)?.response?.data?.error || 'Failed to load Host Agents'}</div>}
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-dark-700/50 text-gray-400 text-xs uppercase"><tr>
                    <th className="px-4 py-3 text-left">Agent</th><th className="px-4 py-3 text-left">Health / socket</th>
                    <th className="px-4 py-3 text-left">Current load</th><th className="px-4 py-3 text-left">Capacity</th><th className="px-4 py-3 text-left">Allocations</th>
                    <th className="px-4 py-3 text-right">Actions</th>
                  </tr></thead>
                  <tbody className="divide-y divide-dark-700">
                    {hostAgentsQuery.isLoading ? <tr><td colSpan={6} className="p-8"><div className="spinner w-8 h-8 mx-auto" /></td></tr> : hostAgents.map(agent => {
                      const weightPercent = agent.capacity.totalWeight > 0 ? Math.round(agent.capacity.usedWeight / agent.capacity.totalWeight * 100) : 0;
                      return <tr key={agent._id} className="hover:bg-dark-700/30 align-top">
                        <td className="px-4 py-3"><div className="font-medium text-gray-200">{agent.name}</div><code className="text-xs text-gray-500">{agent.ipAddress}:{agent.port}</code><div className="text-xs text-gray-600 mt-1">{agent.provider || 'unknown'} · {agent.region || 'N/A'} · {agent.isActive ? 'enabled' : 'disabled'} · custom {agent.customWorkloadsEnabled ? 'opted-in' : 'off'}</div></td>
                        <td className="px-4 py-3"><StatusLine label="REST" status={agent.health.status} good={agent.health.reachable} /><StatusLine label="Socket" status={agent.connection.status} good={agent.connection.connected} />{agent.connection.error && <div className="text-xs text-red-400 max-w-52 truncate" title={agent.connection.error}>{agent.connection.error}</div>}</td>
                        <td className="px-4 py-3"><HostLoadDisplay agent={agent} compact /></td>
                        <td className="px-4 py-3 min-w-64 space-y-2">
                          <div><div className="flex justify-between text-xs text-gray-400"><span>{agent.capacity.usedWeight} / {agent.capacity.totalWeight} weight</span><span>{weightPercent}%</span></div><div className="progress-bar mt-1"><div className={`progress-fill ${weightPercent > 90 ? 'progress-fill-danger' : ''}`} style={{ width: `${Math.min(weightPercent, 100)}%` }} /></div></div>
                          <HardCapacityLine label="CPU reserved" reserved={agent.capacity.hard.reserved.nanoCpus} total={agent.capacity.hard.total.nanoCpus} available={agent.capacity.hard.available.nanoCpus} game={agent.capacity.hard.gameReserved.nanoCpus} custom={agent.capacity.hard.customReserved.nanoCpus} format={value => `${(value / 1_000_000_000).toFixed(2)} vCPU`} />
                          <HardCapacityLine label="RAM reserved" reserved={agent.capacity.hard.reserved.memoryBytes} total={agent.capacity.hard.total.memoryBytes} available={agent.capacity.hard.available.memoryBytes} game={agent.capacity.hard.gameReserved.memoryBytes} custom={agent.capacity.hard.customReserved.memoryBytes} format={formatHostLoadBytes} />
                          <HardCapacityLine label="Storage reserved" reserved={agent.capacity.hard.reserved.storageBytes} total={agent.capacity.hard.total.storageBytes} available={agent.capacity.hard.available.storageBytes} game={agent.capacity.hard.gameReserved.storageBytes} custom={agent.capacity.hard.customReserved.storageBytes} format={formatHostLoadBytes} />
                          <div className="text-xs text-gray-500">revision {agent.capacity.revision}</div>
                        </td>
                        <td className="px-4 py-3"><div className="text-gray-300">{agent.capacity.allocatedServerCount} servers</div><div className="text-xs text-gray-500">{agent.capacity.reservationCount} game · {agent.capacity.customReservationCount} custom reservations</div><button className="text-xs text-neon-400 mt-1 hover:underline" onClick={() => setDetailAgentId(agent._id)}>View ledger</button></td>
                        <td className="px-4 py-3"><div className="flex justify-end flex-wrap gap-1 max-w-64 ml-auto">
                          <IconButton title="Edit" onClick={() => { setEditHostAgent(agent); setShowAgentForm(true); }}><Pencil className="w-4 h-4" /></IconButton>
                          <IconButton title="Health check" onClick={() => actionMutation.mutate({ agent, action: 'check-health' })}><RefreshCw className={`w-4 h-4 ${actionMutation.isPending ? 'animate-spin' : ''}`} /></IconButton>
                          {agent.isActive ? <IconButton title="Disable and disconnect" onClick={() => actionMutation.mutate({ agent, action: 'disconnect' })}><Unplug className="w-4 h-4" /></IconButton> : <IconButton title="Enable and connect" onClick={() => actionMutation.mutate({ agent, action: 'connect' })}><Plug className="w-4 h-4" /></IconButton>}
                          <IconButton title="Capacity and reconciliation" onClick={() => setDetailAgentId(agent._id)}><Eye className="w-4 h-4" /></IconButton>
                          <IconButton title="Rotate or set token" onClick={() => rotateToken(agent)}><Key className="w-4 h-4" /></IconButton>
                          <IconButton title="Open SSH" onClick={() => setSshAgent(agent)}><Terminal className="w-4 h-4" /></IconButton>
                          <IconButton title="Delete Agent" onClick={() => confirm(`Delete ${agent.name}? Only Agents without assigned servers can be deleted.`) && actionMutation.mutate({ agent, action: 'delete' })}><Trash2 className="w-4 h-4 text-red-400" /></IconButton>
                        </div></td>
                      </tr>;
                    })}
                    {!hostAgentsQuery.isLoading && hostAgents.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-gray-500">No Host Agents found</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>{pagination?.total || 0} Agents total</span>
              <div className="flex items-center gap-2"><button className="btn-ghost p-2" disabled={page <= 1} onClick={() => setPage(value => Math.max(1, value - 1))}><ChevronLeft className="w-4 h-4" /></button><span>{pagination?.page || page} / {pagination?.totalPages || 1}</span><button className="btn-ghost p-2" disabled={(pagination?.page || page) >= (pagination?.totalPages || 1)} onClick={() => setPage(value => value + 1)}><ChevronRight className="w-4 h-4" /></button><select className="input w-20" value={pageSize} onChange={event => { setPageSize(Number(event.target.value)); setPage(1); }}><option value={10}>10</option><option value={20}>20</option><option value={50}>50</option></select></div>
            </div>
          </div>
        )}

        {activeTab === 'packages' && <PackageManager />}
        {activeTab === 'custom-products' && <CustomProductsManager
          initialProductId={searchParams.get('productId') || undefined}
          initialSection={searchParams.get('section') || undefined}
          onNavigationChange={setCustomProductNavigation}
        />}
        {activeTab === 'diagnostics' && <OperationDiagnostics />}
        {activeTab === 'commands' && <CommandTemplatesManager />}
      </div>

      {showAgentForm && <AddHostAgentModal hostAgent={editHostAgent || undefined} onClose={() => { setShowAgentForm(false); setEditHostAgent(null); }} onSuccess={async result => {
        setShowAgentForm(false); setEditHostAgent(null);
        if (result.agentToken) setOneTimeSecret({ title: `Token for ${result.hostAgent.name}`, value: result.agentToken });
        setMessage({ kind: 'success', text: `Host Agent ${result.hostAgent.name} saved.` });
        await queryClient.invalidateQueries({ queryKey: ['host-agents'] });
      }} />}
      {detailAgentId && <HostAgentDetailsModal hostAgentId={detailAgentId} onClose={() => setDetailAgentId(null)} />}
      {sshAgent && <SSHModal hostAgent={sshAgent} onClose={() => setSshAgent(null)} />}
      {oneTimeSecret && <SecretModal title={oneTimeSecret.title} value={oneTimeSecret.value} onClose={() => setOneTimeSecret(null)} />}
    </div>
  );
}

function Filter({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return <label className="flex-1 min-w-36 text-xs text-gray-500">{label}<input className="input mt-1" value={value} onChange={event => onChange(event.target.value)} /></label>;
}

function StatusLine({ label, status, good }: { label: string; status: string; good: boolean }) {
  return <div className="flex items-center gap-2 text-xs mb-1"><span className={good ? 'status-dot-online' : 'status-dot-offline'} /><span className="text-gray-500 w-12">{label}</span><span className={good ? 'text-neon-400' : 'text-gray-400'}>{status}</span></div>;
}

function HardCapacityLine({
  label,
  reserved,
  total,
  available,
  game,
  custom,
  format,
}: {
  label: string;
  reserved: number;
  total: number;
  available: number;
  game: number;
  custom: number;
  format: (value: number) => string;
}) {
  const percent = total > 0 ? Math.round((reserved / total) * 100) : 0;
  return <div><div className="flex justify-between gap-3 text-[11px] text-gray-500"><span>{label}</span><span>{format(reserved)} / {format(total)}</span></div><div className="progress-bar mt-1"><div className={`progress-fill ${percent >= 100 ? 'progress-fill-danger' : ''}`} style={{ width: `${Math.min(Math.max(percent, 0), 100)}%` }} /></div><div className="text-[10px] text-gray-600 mt-0.5">available {format(available)} · game {format(game)} · custom {format(custom)}</div></div>;
}

function IconButton({ title, onClick, children }: { title: string; onClick: () => void; children: React.ReactNode }) {
  return <button className="btn-ghost p-2" title={title} onClick={onClick}>{children}</button>;
}

function SecretModal({ title, value, onClose }: { title: string; value: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  return <div className="modal-overlay" onClick={onClose}><div className="modal-content max-w-xl p-6" onClick={event => event.stopPropagation()}><div className="flex justify-between items-center"><h2 className="text-lg font-bold text-gray-100">{title}</h2><button className="btn-ghost p-2" onClick={onClose}><X className="w-4 h-4" /></button></div><p className="text-sm text-yellow-300 mt-3">This secret is shown only in this response. Save it now and update the Agent process.</p><div className="mt-4 flex gap-2"><code className="flex-1 p-3 bg-dark-900 rounded text-xs text-gray-300 break-all select-all">{value}</code><button className="btn-secondary" onClick={async () => { await navigator.clipboard?.writeText(value); setCopied(true); }}><Copy className="w-4 h-4" /> {copied ? 'Copied' : 'Copy'}</button></div><div className="flex justify-end mt-5"><button className="btn-primary" onClick={onClose}>I saved it</button></div></div></div>;
}
